import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Alert } from "@mui/material";
import { quoteAPI } from "../services/api";
import {
  Bell,
  Menu,
  X,
  User,
  LogOut,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ShieldCheck,
  Store,
  Tag,
  CalendarDays,
  FileText,
  ShoppingCart,
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

const parseSizePrices = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) return val.length > 0 ? val : null;
  try {
    const arr = JSON.parse(val);
    return Array.isArray(arr) && arr.length > 0 ? arr : null;
  } catch {
    return null;
  }
};

const parseJSON = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    return JSON.parse(val);
  } catch {
    return [];
  }
};

const fmt = (n) =>
  parseFloat(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusConfig = {
  pending: {
    label: "Pending",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    icon: Clock,
    dot: "bg-amber-500",
  },
  quoted: {
    label: "Quoted",
    color: "bg-green-100 text-green-700 border-green-200",
    icon: CheckCircle,
    dot: "bg-green-500",
  },
  rejected: {
    label: "Rejected",
    color: "bg-red-100 text-red-700 border-red-200",
    icon: XCircle,
    dot: "bg-red-500",
  },
  expired: {
    label: "Expired",
    color: "bg-gray-100 text-gray-500 border-gray-200",
    icon: AlertCircle,
    dot: "bg-gray-400",
  },
};

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// ─── Response Price Card ─────────────────────────────────────────────────────

const ResponseCard = ({ response, navigate }) => {
  const sizePrices = parseSizePrices(response.size_prices);
  const isAdmin = response.quoted_by === "admin";
  const sourceName = isAdmin
    ? "lohamart"
    : response.supplier?.company_name || "Supplier";

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Response Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${isAdmin ? "bg-primary-700" : "bg-secondary-600"}`}
      >
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <ShieldCheck className="w-4 h-4 text-white" />
          ) : (
            <Store className="w-4 h-4 text-white" />
          )}
          <span className="text-sm font-bold text-white">
            Quote from {sourceName}
          </span>
        </div>
        <div className="flex items-center gap-3 text-white/90 text-xs">
          <span className="flex items-center gap-1">
            <CalendarDays className="w-3.5 h-3.5" />
            Valid {response.validity_days} days
          </span>
          <span className="font-bold text-base text-white">
            ₹{fmt(response.total_price)}
          </span>
        </div>
      </div>

      {/* Size-wise Price Breakdown */}
      <div className="overflow-x-auto">
        {sizePrices ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {sizePrices.some((sp) => sp.product_name) && (
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Product
                  </th>
                )}
                {sizePrices.some((sp) => sp.brand_name) && (
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Brand
                  </th>
                )}
                <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Size / Item
                </th>
                <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Qty
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Price / Unit
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sizePrices.map((sp, i) => {
                const total =
                  parseFloat(sp.total) ||
                  parseFloat(sp.price_per_unit) * parseFloat(sp.quantity);
                const hasProductCol = sizePrices.some((s) => s.product_name);
                const hasBrandCol = sizePrices.some((s) => s.brand_name);
                return (
                  <tr key={i} className="hover:bg-gray-50/60 transition-colors">
                    {hasProductCol && (
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {sp.product_name || '—'}
                      </td>
                    )}
                    {hasBrandCol && (
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {sp.brand_name || '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {sp.size}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {sp.quantity}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      ₹{fmt(sp.price_per_unit)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      ₹{fmt(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr
                className={`border-t-2 ${isAdmin ? "border-primary-200 bg-primary-50" : "border-secondary-200 bg-secondary-50"}`}
              >
                <td
                  colSpan={3}
                  className="px-4 py-3 font-bold text-gray-800 text-sm"
                >
                  Grand Total
                </td>
                <td
                  className={`px-4 py-3 text-right font-extrabold text-base ${isAdmin ? "text-primary-700" : "text-secondary-700"}`}
                >
                  ₹{fmt(response.total_price)}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          /* Single price (no size breakdown) */
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
            <span className="text-sm text-gray-600">Price per unit</span>
            <span className="font-bold text-gray-800">
              ₹{fmt(response.price_per_unit)}
            </span>
          </div>
        )}
      </div>

      {/* Remarks + Action */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50 gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {response.remarks ? (
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">Note: </span>
              {response.remarks}
            </p>
          ) : (
            <p className="text-xs text-gray-400 italic">
              No additional remarks
            </p>
          )}
        </div>
        <button
          onClick={() => navigate(`/place-order?response=${response.id}`)}
          className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg transition-colors whitespace-nowrap shadow-sm"
        >
          <ShoppingCart className="w-4 h-4" />
          Place Order
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await quoteAPI.getQuotes();
      if (response.data.success) setQuotes(response.data.data);
    } catch (err) {
      setError("Failed to fetch quotes");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-16">
          <div className="cursor-pointer" onClick={() => navigate("/")}>
            <img
              src="/logo.png"
              alt="Loha Mart"
              className="h-10 w-auto object-contain"
            />
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            {[
              ["Products", "/products"],
              ["Orders", "/orders"],
              ["Quotes", "/quotes"],
              ["Finance", "/finance"],
            ].map(([label, path]) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`text-sm font-medium transition-colors ${window.location.pathname === path ? "text-primary-600" : "text-gray-600 hover:text-primary-600"}`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="flex items-center space-x-4">
            <Bell className="w-5 h-5 text-gray-600" />
            <div className="hidden md:flex items-center space-x-3 pl-3 border-l">
              <div className="text-right">
                <p className="text-sm font-medium">
                  {user?.customer?.company_name || "User"}
                </p>
                <p className="text-xs text-gray-500">{user?.mobile}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <button onClick={handleLogout}>
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden px-4 pb-4 space-y-2 border-t border-gray-100">
            {[
              ["Products", "/products"],
              ["Orders", "/orders"],
              ["Quotes", "/quotes"],
              ["Finance", "/finance"],
            ].map(([label, path]) => (
              <button
                key={path}
                onClick={() => {
                  navigate(path);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {label}
              </button>
            ))}
            <button
              onClick={handleLogout}
              className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {/* ── Page Content ── */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              My Quote Requests
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Track pricing responses from lohamart and suppliers
            </p>
          </div>
          <button
            onClick={() => navigate("/products")}
            className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-white text-sm font-semibold rounded-xl hover:bg-primary-800 transition-colors"
          >
            <FileText className="w-4 h-4" /> New Quote
          </button>
        </div>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <CircularProgress />
          </div>
        ) : quotes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Package className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">
              No quote requests yet
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Browse products and request quotes to get competitive pricing
            </p>
            <button
              onClick={() => navigate("/products")}
              className="px-5 py-2.5 bg-primary-700 text-white text-sm font-semibold rounded-xl hover:bg-primary-800 transition-colors"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {quotes.map((quote) => {
              const cfg = statusConfig[quote.status] || statusConfig.pending;
              const StatusIcon = cfg.icon;
              const selectedItems = parseJSON(quote.selected_items);
              const selectedBrands = parseJSON(quote.selected_brands);
              const hasResponses = quote.responses?.length > 0;

              return (
                <div
                  key={quote.id}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  {/* Quote Header */}
                  <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Package className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-base leading-tight">
                          {quote.product?.name || "Product"}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {quote.product?.category?.name && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              <Tag className="w-3 h-3" />{" "}
                              {quote.product.category.name}
                            </span>
                          )}
                          <span className="text-xs text-gray-400 font-mono">
                            #{quote.quote_number}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(quote.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${cfg.color}`}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {cfg.label}
                    </span>
                  </div>

                  <div className="px-5 py-4 space-y-4">
                    {/* Requested Items */}
                    {selectedItems.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                          Items You Requested
                        </p>
                        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left px-3 py-2 text-xs text-gray-500 font-semibold">
                                  Size
                                </th>
                                <th className="text-right px-3 py-2 text-xs text-gray-500 font-semibold">
                                  Qty Requested
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedItems.map((item, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 text-gray-700 font-medium">
                                    {item.size}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {item.quantity}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Brands requested */}
                    {selectedBrands.length > 0 && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs font-semibold text-gray-500">
                          Brands:
                        </span>
                        {selectedBrands.map((b, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1.5 text-xs bg-secondary-50 text-secondary-700 border border-secondary-200 px-2.5 py-1 rounded-full font-medium"
                          >
                            {b.logo_url && (
                              <img
                                src={
                                  b.logo_url.startsWith("http")
                                    ? b.logo_url
                                    : `${b.logo_url}`
                                }
                                alt={b.brand_name}
                                className="w-4 h-4 object-contain rounded"
                              />
                            )}
                            {b.brand_name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    {quote.customer_notes && (
                      <p className="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <span className="font-semibold text-amber-700">
                          Your Note:{" "}
                        </span>
                        {quote.customer_notes}
                      </p>
                    )}

                    {/* ── QUOTE RESPONSES ── */}
                    {hasResponses ? (
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                          Price Quote{quote.responses.length > 1 ? "s" : ""}{" "}
                          Received ({quote.responses.length})
                        </p>
                        <div className="space-y-3">
                          {quote.responses.map((response) => (
                            <ResponseCard
                              key={response.id}
                              response={response}
                              navigate={navigate}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                        <Clock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700">
                          Your request is being reviewed. You'll receive pricing
                          within <strong>24–48 hours</strong>.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Quotes;
